import { useState, useEffect } from 'react';
import { api, type Role, type PermissionDef, isSuperAdmin } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ShieldPlus, Edit, Trash2, Lock } from 'lucide-react';

export function RoleManagementPage() {
  const { me } = useAuth();
  const isSuper = isSuperAdmin(me?.permissions);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allPerms, setAllPerms] = useState<PermissionDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [editMode, setEditMode] = useState<'create' | 'edit' | null>(null);
  const [editId, setEditId] = useState('');
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPerms, setEditPerms] = useState<Set<string>>(new Set());
  const [editIsBuiltIn, setEditIsBuiltIn] = useState(false);
  const [editIsWildcard, setEditIsWildcard] = useState(false);

  async function loadData() {
    try {
      setLoading(true);
      const [r, p] = await Promise.all([
        api.get<{ roles: Role[] }>('/admin/roles'),
        api.get<{ permissions: PermissionDef[] }>('/admin/roles/permissions'),
      ]);
      setRoles(r.roles);
      setAllPerms(p.permissions);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadData(); }, []);

  function openCreate() {
    setEditMode('create');
    setEditId('');
    setEditName('');
    setEditDesc('');
    setEditPerms(new Set());
    setEditIsBuiltIn(false);
    setEditIsWildcard(false);
  }

  function openEdit(role: Role) {
    setEditMode('edit');
    setEditId(role.id);
    setEditName(role.name);
    setEditDesc(role.description ?? '');
    setEditPerms(new Set(role.permissions.filter((p) => p !== '*')));
    setEditIsBuiltIn(role.isBuiltIn);
    setEditIsWildcard(role.permissions.includes('*'));
  }

  function togglePerm(key: string) {
    setEditPerms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleGroup(group: string) {
    const groupPerms = allPerms.filter((p) => p.group === group).map((p) => p.key);
    const allSelected = groupPerms.every((p) => editPerms.has(p));
    setEditPerms((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        groupPerms.forEach((p) => next.delete(p));
      } else {
        groupPerms.forEach((p) => next.add(p));
      }
      return next;
    });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const permissions = Array.from(editPerms);
    try {
      if (editMode === 'create') {
        await api.post('/admin/roles', { name: editName, description: editDesc, permissions });
      } else {
        await api.put(`/admin/roles/${editId}`, {
          ...(!editIsBuiltIn ? { name: editName } : {}),
          description: editDesc,
          ...(!editIsWildcard ? { permissions } : {}),
        });
      }
      setEditMode(null);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  async function handleDelete(role: Role) {
    if (!confirm(`Rolle "${role.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/roles/${role.id}`);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  // Permissions nach Gruppe gruppieren
  const groups = allPerms.reduce<Record<string, PermissionDef[]>>((acc, p) => {
    (acc[p.group] ??= []).push(p);
    return acc;
  }, {});

  if (loading) return <div className="p-6 text-slate-500">Lade Rollen...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Rollen-Verwaltung</h1>
          <p className="text-sm text-slate-500 mt-1">{roles.length} Rollen definiert</p>
        </div>
        {isSuper && (
          <button onClick={openCreate} className="btn btn-primary flex items-center gap-2">
            <ShieldPlus size={16} /> Neue Rolle
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

      {/* Rollen-Karten */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => (
          <div key={role.id} className="card p-5 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-800">{role.name}</h3>
                  {role.isBuiltIn && (
                    <span className="flex items-center gap-1 text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                      <Lock size={10} /> Built-in
                    </span>
                  )}
                </div>
                {role.description && <p className="text-sm text-slate-500 mt-1">{role.description}</p>}
              </div>
              {isSuper && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(role)} className="p-1.5 rounded hover:bg-slate-200 text-slate-500" title="Bearbeiten">
                    <Edit size={15} />
                  </button>
                  {!role.isBuiltIn && (
                    <button onClick={() => void handleDelete(role)} className="p-1.5 rounded hover:bg-red-100 text-red-500" title="Löschen">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500">
              {role._count?.users ?? 0} Benutzer zugewiesen
            </div>
            <div className="flex flex-wrap gap-1">
              {role.permissions.includes('*') ? (
                <span className="bg-rla-100 text-rla-700 text-xs px-2 py-0.5 rounded-full font-medium">Alle Rechte</span>
              ) : (
                role.permissions.slice(0, 6).map((p) => (
                  <span key={p} className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{p}</span>
                ))
              )}
              {!role.permissions.includes('*') && role.permissions.length > 6 && (
                <span className="text-xs text-slate-400">+{role.permissions.length - 6} weitere</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      {editMode && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setEditMode(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={(e) => void handleSave(e)} className="space-y-5">
              <h2 className="text-lg font-semibold text-slate-800">
                {editMode === 'create' ? 'Neue Rolle' : 'Rolle bearbeiten'}
              </h2>

              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  required
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input"
                  disabled={editIsBuiltIn}
                  placeholder="z.B. Cloud-Verwalter"
                />
              </div>

              <div>
                <label className="label">Beschreibung</label>
                <input
                  type="text"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  className="input"
                  placeholder="Kurze Beschreibung der Rolle"
                />
              </div>

              {editIsWildcard ? (
                <div className="bg-rla-50 text-rla-700 px-4 py-3 rounded-lg text-sm">
                  Super-Admin hat automatisch alle Berechtigungen. Diese können nicht geändert werden.
                </div>
              ) : (
                <div className="space-y-4">
                  <label className="label">Berechtigungen</label>
                  {Object.entries(groups).map(([group, perms]) => {
                    const allSelected = perms.every((p) => editPerms.has(p.key));
                    const someSelected = perms.some((p) => editPerms.has(p.key));
                    return (
                      <div key={group} className="border border-slate-200 rounded-lg p-3">
                        <label className="flex items-center gap-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                            onChange={() => toggleGroup(group)}
                            className="rounded border-slate-300"
                          />
                          <span className="font-medium text-sm text-slate-700">{group}</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 ml-6">
                          {perms.map((p) => (
                            <label key={p.key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer py-0.5">
                              <input
                                type="checkbox"
                                checked={editPerms.has(p.key)}
                                onChange={() => togglePerm(p.key)}
                                className="rounded border-slate-300"
                              />
                              {p.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setEditMode(null)} className="btn">Abbrechen</button>
                <button type="submit" className="btn btn-primary">
                  {editMode === 'create' ? 'Erstellen' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
